import { createClient } from '@supabase/supabase-js';

// Supabase credentials
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Helper to set cookie with Secure and SameSite=Strict attributes
// const setCookie = (name, value, days = 30) => {
//   let expires = "";
//   if (days) {
//     const date = new Date();
//     date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
//     expires = "; expires=" + date.toUTCString();
//   }
//   const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
//   document.cookie = name + "=" + encodeURIComponent(value || "") + expires + "; path=/; samesite=strict" + (isSecure ? "; secure" : "");
// };

// // Helper to get cookie
// const getCookie = (name) => {
//   const nameEQ = name + "=";
//   const ca = document.cookie.split(';');
//   for (let i = 0; i < ca.length; i++) {
//     let c = ca[i];
//     while (c.charAt(0) === ' ') c = c.substring(1, c.length);
//     if (c.indexOf(nameEQ) === 0) {
//       try {
//         return decodeURIComponent(c.substring(nameEQ.length, c.length));
//       } catch (e) {
//         return c.substring(nameEQ.length, c.length);
//       }
//     }
//   }
//   return null;
// };

// // Helper to erase cookie
// const eraseCookie = (name) => {
//   const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
//   document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; samesite=strict' + (isSecure ? '; secure' : '');
// };

// Derive encryption key dynamically using stable key
const deriveKey = () => {
  return 'tellbrandz-secure-auth-salt-2026';
};

// XOR encryption / decryption
const xorEncryptDecrypt = (input, key) => {
  let output = '';
  for (let i = 0; i < input.length; i++) {
    const charCode = input.charCodeAt(i) ^ key.charCodeAt(i % key.length);
    output += String.fromCharCode(charCode);
  }
  return output;
};

// Encrypt payload (symmetric and safe)
const encryptPayload = (payload) => {
  try {
    const key = deriveKey();
    const encoded = encodeURIComponent(payload);
    const encrypted = xorEncryptDecrypt(encoded, key);
    return btoa(encrypted);
  } catch (e) {
    return payload;
  }
};

// Decrypt payload (symmetric and safe)
const decryptPayload = (encrypted) => {
  try {
    const key = deriveKey();
    const decrypted = xorEncryptDecrypt(atob(encrypted), key);
    return decodeURIComponent(decrypted);
  } catch (e) {
    return null;
  }
};

// Key obfuscation (simple DJB2 hash)
const hashKey = (key) => {
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 33) ^ key.charCodeAt(i);
  }
  return 'tb_sec_' + Math.abs(hash).toString(16);
};

// Custom secure storage handler using encrypted localStorage to bypass cookie size limitations
const createSecureStorage = (storageKey) => {
  const memoryStorage = new Map();
  return {
    getItem: (key) => {
      try {
        const hashedKey = hashKey(key);
        const localVal = typeof window !== 'undefined' ? window.localStorage.getItem(hashedKey) : null;
        if (localVal) {
          const decrypted = decryptPayload(localVal);
          if (decrypted) {
            try {
              const parsed = JSON.parse(decrypted);
              if (parsed && parsed.access_token && typeof parsed.access_token === 'string') {
                if (parsed.access_token.split('.').length !== 3) {
                  console.warn('[SecureStorage] Malformed JWT token detected. Purging invalid token from storage.');
                  if (typeof window !== 'undefined') {
                    window.localStorage.removeItem(hashedKey);
                  }
                  return null;
                }
              }
            } catch (e) {
              // Not JSON
            }
            return decrypted;
          }
        }

        return memoryStorage.get(hashedKey) ?? null;
      } catch (e) {
        const hashedKey = hashKey(key);
        return memoryStorage.get(hashedKey) ?? null;
      }
    },
    setItem: (key, value) => {
      try {
        const hashedKey = hashKey(key);
        const encrypted = encryptPayload(value);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(hashedKey, encrypted);
        }
      } catch (e) {
        const hashedKey = hashKey(key);
        memoryStorage.set(hashedKey, value);
      }
    },
    removeItem: (key) => {
      try {
        const hashedKey = hashKey(key);
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(hashedKey);
        }
      } catch (e) {
        const hashedKey = hashKey(key);
        memoryStorage.delete(hashedKey);
      }
    }
  };
};

// Create Supabase client with hardened Auth storage and PKCE configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false,
    flowType: 'pkce',
    storageKey: 'tellbrandz-admin-auth-token',
    storage: createSecureStorage('tellbrandz-admin-auth-token')
  },
  global: {
    headers: {
      'X-Client-Info': 'tellbrandz-admin'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Expose URL (if needed in future)
supabase.supabaseUrl = supabaseUrl;

let adminLogoutInFlight = false;

const clearAdminLocalSession = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem("admin_session");
  localStorage.removeItem("admin_system_role");
  localStorage.removeItem("admin_allowed_sections");
  localStorage.removeItem("admin_staff_id");
  localStorage.removeItem("admin_telemetry_session_id");
  localStorage.removeItem("admin_new_login");
};

const readAdminSessionUserId = () => {
  try {
    const raw = localStorage.getItem("admin_session");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user?.id || parsed?.session?.user?.id || null;
  } catch {
    return null;
  }
};

/** True when edge/API payload indicates missing/expired/invalid admin JWT. */
export const isUnauthorizedAuthError = (error, data) => {
  const parts = [
    error?.message,
    error?.context?.message,
    typeof data?.error === 'string' ? data.error : null,
    typeof data?.message === 'string' ? data.message : null,
    typeof data?.reason === 'string' ? data.reason : null,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (!parts) return false;

  return (
    parts.includes('unauthorized') ||
    parts.includes('auth session missing') ||
    parts.includes('session expired') ||
    parts.includes('jwt') ||
    parts.includes('not authenticated') ||
    parts.includes('invalid or missing user session') ||
    parts.includes('token is missing') ||
    parts.includes('malformed') ||
    parts.includes('sign in again')
  );
};

/** Force admin logout when access/refresh token is dead. Safe to call multiple times. */
export const forceAdminLogout = async (reason = 'session_expired') => {
  if (typeof window === 'undefined') return;
  if (adminLogoutInFlight) return;

  const path = window.location.pathname || '';
  if (path.startsWith('/login') || path.startsWith('/logout') || path.startsWith('/reset-password')) {
    clearAdminLocalSession();
    return;
  }

  adminLogoutInFlight = true;
  console.warn('[Auth] Forcing admin logout:', reason);
  try {
    clearAdminLocalSession();
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) {
      // Ignore signOut errors during forced logout
    }
    const qs = reason ? `?error=${encodeURIComponent(reason)}` : '';
    window.location.replace(`/login${qs}`);
  } finally {
    // Keep flag true until navigation completes to avoid stacked redirects
  }
};

const syncAdminSessionCopy = (session) => {
  if (typeof window === 'undefined' || !session) return;
  try {
    const stored = localStorage.getItem("admin_session");
    const parsed = stored ? JSON.parse(stored) : {};
    parsed.user = session.user || parsed.user || null;
    parsed.session = {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      expires_at: session.expires_at,
      expires_in: session.expires_in,
      token_type: session.token_type,
      user: session.user
    };
    localStorage.setItem("admin_session", JSON.stringify(parsed));
  } catch (e) {
    // non-fatal
  }
};

// Keep admin_session in sync whenever Supabase refreshes/replaces tokens.
if (typeof window !== 'undefined') {
  supabase.auth.onAuthStateChange(async (event, session) => {
    if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
      if (session) syncAdminSessionCopy(session);
    }
    if (event === 'SIGNED_OUT') {
      // Only hard-redirect when we still believe the user was logged in
      const hadAdminSession = !!localStorage.getItem("admin_session");
      clearAdminLocalSession();
      if (hadAdminSession && !adminLogoutInFlight) {
        await forceAdminLogout('signed_out');
      }
    }
  });
}

// Proactive Session Refresh Helper with localStorage fallback
export const refreshSessionIfExpired = async ({ forceLogout = true } = {}) => {
  try {
    let { data: { session }, error } = await supabase.auth.getSession();

    // Fallback: If memory/storage session is empty, restore from admin_session in localStorage
    if ((error || !session) && typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem("admin_session");
        if (stored) {
          const parsed = JSON.parse(stored);
          const rawToken = parsed?.session?.access_token;
          const refreshToken = parsed?.session?.refresh_token;
          if (rawToken && typeof rawToken === 'string' && rawToken.split('.').length === 3 && refreshToken) {
            const { data: setRes, error: setErr } = await supabase.auth.setSession({
              access_token: rawToken,
              refresh_token: refreshToken
            });
            if (setErr) {
              console.warn('[Supabase] Fallback setSession failed — logging out:', setErr.message);
              if (forceLogout) await forceAdminLogout('session_expired');
              return null;
            }
            if (setRes?.session) {
              session = setRes.session;
              syncAdminSessionCopy(session);
            }
          }
        }
      } catch (e) {
        console.warn('Fallback session restoration notice:', e);
      }
    }

    if (!session) {
      // Stale admin_session without a live Supabase session → force re-login
      if (forceLogout && typeof window !== 'undefined' && localStorage.getItem("admin_session")) {
        await forceAdminLogout('session_expired');
      }
      return null;
    }

    if (session.access_token && typeof session.access_token === 'string') {
      if (session.access_token.split('.').length !== 3) {
        console.warn('[Supabase] Malformed JWT access token encountered. Logging out.');
        if (forceLogout) await forceAdminLogout('invalid_token');
        return null;
      }
    }

    const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
    const alreadyExpired = expiresAt && expiresAt <= Date.now();
    const nearExpiry = expiresAt && expiresAt - Date.now() < 300000;

    if (alreadyExpired || nearExpiry) {
      const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr || !refreshed?.session) {
        console.warn('[Supabase] Refresh token failed — logging out:', refreshErr?.message || 'no session');
        if (forceLogout) await forceAdminLogout('session_expired');
        return null;
      }
      syncAdminSessionCopy(refreshed.session);
      return refreshed.session;
    }

    return session;
  } catch (err) {
    console.error('Session refresh error:', err);
    if (forceLogout) await forceAdminLogout('session_expired');
    return null;
  }
};

/**
 * Require a live JWT that Auth server accepts and that matches admin_session user.
 * On failure, force logout (unless forceLogout: false).
 */
export const ensureAuthenticatedSession = async ({ forceLogout = true } = {}) => {
  try {
    let session = await refreshSessionIfExpired({ forceLogout });
    if (!session?.access_token || !session?.user?.id) {
      if (forceLogout) await forceAdminLogout('missing_jwt');
      return null;
    }

    // Validate token with Auth (catches revoked / dead JWTs that still look present locally)
    const { data: userData, error: userError } = await supabase.auth.getUser(session.access_token);
    if (userError || !userData?.user?.id) {
      // One refresh attempt, then hard fail
      const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr || !refreshed?.session?.access_token) {
        if (forceLogout) await forceAdminLogout('jwt_rejected');
        return null;
      }
      const { data: retryUser, error: retryErr } = await supabase.auth.getUser(
        refreshed.session.access_token
      );
      if (retryErr || !retryUser?.user?.id) {
        if (forceLogout) await forceAdminLogout('jwt_rejected');
        return null;
      }
      session = refreshed.session;
      syncAdminSessionCopy(session);
    }

    const adminUserId = readAdminSessionUserId();
    if (adminUserId && adminUserId !== session.user.id) {
      if (forceLogout) await forceAdminLogout('jwt_user_mismatch');
      return null;
    }

    syncAdminSessionCopy(session);
    return session;
  } catch (err) {
    console.error('[Auth] ensureAuthenticatedSession failed:', err);
    if (forceLogout) await forceAdminLogout('ensure_session_exception');
    return null;
  }
};

// Preserve raw invoke so our wrapper can call it without recursion
const rawFunctionsInvoke = supabase.functions.invoke.bind(supabase.functions);

// Helper for invoking Edge Functions with guaranteed user access token
export const invokeEdgeFunction = async (functionName, options = {}) => {
  const session = await ensureAuthenticatedSession({ forceLogout: true });
  const token = session?.access_token;

  if (!token || typeof token !== 'string' || token.split('.').length !== 3) {
    console.warn('[invokeEdgeFunction] User JWT missing or unauthenticated. Forcing logout.');
    await forceAdminLogout('session_expired');
    return {
      data: null,
      error: {
        message: 'Your session expired. Please sign in again.'
      }
    };
  }

  const mergedHeaders = {
    Authorization: `Bearer ${token}`,
    ...(options.headers || {})
  };

  const result = await rawFunctionsInvoke(functionName, {
    ...options,
    headers: mergedHeaders
  });

  // Edge functions often return 401/500 with { error: "Unauthorized..." } in the body
  if (isUnauthorizedAuthError(result?.error, result?.data)) {
    await forceAdminLogout('unauthorized_api');
    return {
      data: null,
      error: {
        message: 'Your session expired. Please sign in again.'
      }
    };
  }

  return result;
};

// Route all direct supabase.functions.invoke calls through JWT-safe wrapper
supabase.functions.invoke = (functionName, options = {}) =>
  invokeEdgeFunction(functionName, options);

// -------------------------
// TEST CONNECTION FUNCTION
// -------------------------
export const testConnection = async () => {
  try {
    const { error } = await supabase
      .from('users')
      .select('id')
      .limit(1);

    if (error) {
      return {
        success: false,
        message: `Connection failed: ${error.message}`
      };
    }

    return {
      success: true,
      message: 'Connected to Supabase successfully'
    };
  } catch (err) {
    return {
      success: false,
      message: err.message || 'Network error'
    };
  }
};

// -------------------------
// HEALTH CHECK FUNCTION
// -------------------------
export const healthCheck = async () => {
  try {
    const session = await refreshSessionIfExpired();
    const { data, error } = await supabase.auth.getUser();

    return {
      authWorking: !error,
      loggedIn: data?.user ? true : false,
      sessionActive: !!session,
      timestamp: new Date().toISOString()
    };
  } catch (err) {
    return {
      authWorking: false,
      loggedIn: false,
      sessionActive: false,
      message: err.message,
      timestamp: new Date().toISOString()
    };
  }
};

